import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Pencil,
  Trash2,
  WalletCards,
  UserMinus,
  AlertCircle,
  MapPin,
  Copy,
} from "lucide-react";

import { getVenueById, updateVenue } from "./venuesApi";
import VenueGroundForm from "./VenueGroundForm";
import PricingModal from "./PricingModal";
import VenueWebsiteBuilderModal from "./VenueWebsiteBuilderModal";
import { copyTextToClipboard } from "./copyTextToClipboard";
import {
  buildPublicVenueUrl,
} from "../../PublicVenue/publicVenueHostname";

import ConfirmDialog from "../../../components/common/ConfirmDialog";
import ReusableModal from "../../../components/common/ReusableModal";

import FormButton from "../../../components/form/FormButton";
import FormField from "../../../components/form/FormField";
import FormInput from "../../../components/form/FormInput";
import FormSelect from "../../../components/form/FormSelect";

import {
  listAssignedVenueAdmins,
  createAndAssignVenueAdmin,
  assignExistingVenueAdmin,
  unassignVenueAdmin,
  listAllVenueAdmins,
  updateVenueAdmin,
} from "./venueAdminApi";

import {
  presignMediaByVenueId,
  confirmMediaByVenueId,
  getVenueMedia,
  deleteBannerBySlug,
  deleteImageByKey,
} from "./venueMediaApi";

import {
  listVenueGrounds,
  createVenueGround,
  updateVenueGround,
  deleteVenueGround,
} from "./venueGroundsApi";

import useTableController from "../../../components/table/useTableController";
import Paginator from "../../../components/table/Paginator";

const tagClass = (t) => {
  const k = String(t || "").toLowerCase();
  if (k === "featured") return "sa-tag sa-tag--featured";
  if (k === "premium") return "sa-tag sa-tag--premium";
  if (k === "popular") return "sa-tag sa-tag--popular";
  return "sa-tag";
};

const formatContactPhone = (contact) => {
  if (!contact) return "—";

  if (contact.e164) return contact.e164;

  if (contact.dialCode && contact.nationalNumber) {
    return `+${contact.dialCode} ${contact.nationalNumber}`;
  }

  return contact.nationalNumber || "—";
};

const CreateAssignVenueAdminModal = ({
  open,
  values,
  saving,
  onChange,
  onClose,
  onSubmit,
}) => {
  return (
    <ReusableModal
      open={open}
      title="Create & Assign Venue Admin"
      width="720px"
      className="va-create-assign-modal"
      closeOnBackdrop={false}
      onClose={() => {
        if (!saving) onClose?.();
      }}
    >
      <form className="va-form" onSubmit={onSubmit} noValidate>
        <div className="va-sub">
          This will create a new admin account and immediately assign it to this venue.
        </div>

        <div className="va-grid">
          <FormField label="Name">
            <FormInput
              type="text"
              value={values.name}
              onChange={(event) =>
                onChange((prev) => ({ ...prev, name: event.target.value }))
              }
              placeholder="Anita Manager"
              required
            />
          </FormField>

          <FormField label="Email">
            <FormInput
              type="email"
              value={values.email}
              onChange={(event) =>
                onChange((prev) => ({ ...prev, email: event.target.value }))
              }
              placeholder="anita@example.com"
              required
            />
          </FormField>

          <FormField
            label="Password"
            hint="Use a simple starter password; they can change it later."
          >
            <FormInput
              type="password"
              value={values.password}
              onChange={(event) =>
                onChange((prev) => ({ ...prev, password: event.target.value }))
              }
              placeholder="Min 6 characters"
              required
            />
          </FormField>

          <div className="va-tip">
            <div className="va-tip-title">Best practice</div>
            <ul className="va-tip-list">
              <li>Use a work email.</li>
              <li>Assign max two admins per venue.</li>
              <li>Keep names human-readable.</li>
            </ul>
          </div>
        </div>

        <div className="va-actions">
          <FormButton type="submit" variant="primary" disabled={saving}>
            {saving ? "Saving..." : "Create & Assign"}
          </FormButton>

          <FormButton
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </FormButton>
        </div>
      </form>
    </ReusableModal>
  );
};

const AssignExistingVenueAdminModal = ({
  open,
  adminSearch,
  assignStaffId,
  availableAdmins,
  saving,
  onSearchChange,
  onSelectAdmin,
  onClose,
  onSubmit,
}) => {
  return (
    <ReusableModal
      open={open}
      title="Assign Existing Venue Admin"
      width="720px"
      className="va-assign-existing-modal"
      closeOnBackdrop={false}
      onClose={() => {
        if (!saving) onClose?.();
      }}
    >
      <div className="va-form">
        <div className="va-sub">
          Pick from the existing admin pool. Already-assigned admins are hidden.
        </div>

        <div className="va-grid">
          <FormField label="Search Admin" className="va-col-span-2">
            <FormInput
              type="text"
              value={adminSearch}
              onChange={onSearchChange}
              placeholder="Search by name or email"
            />
          </FormField>

          <FormField
            label="Choose Admin"
            hint={
              availableAdmins.length > 0
                ? `Available: ${availableAdmins.length}`
                : "No available admins. Create one instead."
            }
            className="va-col-span-2"
          >
            <FormSelect value={assignStaffId} onChange={onSelectAdmin}>
              <option value="">Select admin...</option>
              {availableAdmins.map((admin) => (
                <option key={admin._id} value={admin._id}>
                  {admin.name} — {admin.email}
                </option>
              ))}
            </FormSelect>
          </FormField>
        </div>

        <div className="va-actions">
          <FormButton
            type="button"
            variant="primary"
            disabled={!assignStaffId || saving || availableAdmins.length === 0}
            onClick={onSubmit}
          >
            {saving ? "Assigning..." : "Assign"}
          </FormButton>

          <FormButton
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </FormButton>
        </div>
      </div>
    </ReusableModal>
  );
};

const EditVenueAdminModal = ({
  open,
  values,
  saving,
  onChange,
  onClose,
  onSubmit,
}) => {
  return (
    <ReusableModal
      open={open}
      title="Edit Venue Admin"
      width="720px"
      className="va-edit-admin-modal"
      closeOnBackdrop={false}
      onClose={() => {
        if (!saving) onClose?.();
      }}
    >
      <form className="va-form" onSubmit={onSubmit} noValidate>
        <div className="va-sub">
          Update profile details or toggle active status. Password remains unchanged.
        </div>

        <div className="va-grid">
          <FormField label="Name">
            <FormInput
              type="text"
              value={values.name}
              onChange={(event) =>
                onChange((prev) => ({ ...prev, name: event.target.value }))
              }
              placeholder="Admin name"
              required
            />
          </FormField>

          <FormField label="Email">
            <FormInput
              type="email"
              value={values.email}
              onChange={(event) =>
                onChange((prev) => ({ ...prev, email: event.target.value }))
              }
              placeholder="email@example.com"
              required
            />
          </FormField>

          <FormField
            label="Status"
            hint="Toggling this disables/enables account access. Password is not changed here."
            className="va-col-span-2"
          >
            <div className="va-status-row">
              <label className="sa-switch">
                <input
                  type="checkbox"
                  checked={!!values.active}
                  onChange={(event) =>
                    onChange((prev) => ({
                      ...prev,
                      active: event.target.checked,
                    }))
                  }
                />
                <span />
              </label>

              <span className={`sa-status ${values.active ? "active" : "inactive"}`}>
                {values.active ? "Active" : "Inactive"}
              </span>
            </div>
          </FormField>
        </div>

        <div className="va-actions">
          <FormButton
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </FormButton>

          <FormButton type="submit" variant="primary" disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </FormButton>
        </div>
      </form>
    </ReusableModal>
  );
};

const VenueDetails = () => {
  const { id: venueId } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const basePath = pathname.startsWith("/venue-admin")
    ? "/venue-admin"
    : "/super-admin";

  const toBase = (subpath) => `${basePath}${subpath}`;

  const [venue, setVenue] = useState(null);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [websiteBuilderOpen, setWebsiteBuilderOpen] = useState(false);
  const [websiteSaving, setWebsiteSaving] = useState(false);
  const [pricingGround, setPricingGround] = useState(null);

  const [loadingVenue, setLoadingVenue] = useState(true);
  const [media, setMedia] = useState({ bannerUrl: "", gallery: [], slug: "" });
  const [uploading, setUploading] = useState(false);

  const [grounds, setGrounds] = useState([]);
  const [loadingGrounds, setLoadingGrounds] = useState(true);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const [admins, setAdmins] = useState([]);
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [adminMode, setAdminMode] = useState("create");
  const [newAdmin, setNewAdmin] = useState({
    name: "",
    email: "",
    password: "",
  });
  const [assignStaffId, setAssignStaffId] = useState("");
  const [allAdmins, setAllAdmins] = useState([]);
  const [adminSearch, setAdminSearch] = useState("");
  const [savingAdmin, setSavingAdmin] = useState(false);
  const [editAdmin, setEditAdmin] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    active: true,
  });
  const [editSaving, setEditSaving] = useState(false);
  const [unassignTarget, setUnassignTarget] = useState(null);
  const [unassignSaving, setUnassignSaving] = useState(false);
  const [copiedSlug, setCopiedSlug] = useState(false);

  const [bannerDeleteOpen, setBannerDeleteOpen] = useState(false);
  const [bannerDeleting, setBannerDeleting] = useState(false);

  const [galleryDeleteTarget, setGalleryDeleteTarget] = useState(null);
  const [galleryDeleting, setGalleryDeleting] = useState(false);

  const roleFromPath = pathname.startsWith("/venue-admin")
    ? "venue_admin"
    : pathname.startsWith("/country-admin")
      ? "country_admin"
      : "superadmin";

  let roleFromStorage = "";
  try {
    const ctx = JSON.parse(localStorage.getItem("va_ctx") || "{}");
    roleFromStorage = String(ctx.role || "").trim().toLowerCase();
  } catch (_) { }

  const currentRole = roleFromStorage || roleFromPath;
  const isVenueAdmin = currentRole === "venue_admin";
  const isSuperAdmin = currentRole === "superadmin";

  const openPricing = (ground) => {
    setPricingGround(ground);
    setPricingOpen(true);
  };

  const getPublicVenueUrl = () =>
    buildPublicVenueUrl(
      {
        subdomain: venue?.publicWebsite?.subdomain,
        slug: venue?.slug,
      },
      window.location,
    );

  const copyPublicVenueUrl = async () => {
    const url = getPublicVenueUrl();
    if (!url) {
      toast.error("Public website URL is not available yet.");
      return;
    }

    const copied = await copyTextToClipboard(url);

    if (copied) {
      toast.success("Public website link copied.");
      return;
    }

    toast.error("Unable to copy public website link.");
  };

  const savePublicWebsite = async (publicWebsite) => {
    if (!venue?._id) return;

    if (!isSuperAdmin) {
      toast.error("Only Super Admin can manage venue websites.");
      return;
    }

    try {
      setWebsiteSaving(true);
      const updated = await updateVenue(venue._id, { publicWebsite });
      setVenue(updated);
      setWebsiteBuilderOpen(false);
      toast.success("Public website settings saved.");
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save public website settings."
      );
      throw error;
    } finally {
      setWebsiteSaving(false);
    }
  };

  const closePricing = () => {
    setPricingOpen(false);
    setPricingGround(null);
  };

  const loadVenue = async () => {
    setLoadingVenue(true);

    try {
      const data = await getVenueById(venueId);
      console.log("VENUE DETAILS", data);
      setVenue(data);
    } catch {
      toast.error("Failed to load venue");
    } finally {
      setLoadingVenue(false);
    }
  };

  const loadMedia = async () => {
    try {
      if (!venueId) return;

      const data = await getVenueMedia(venueId);
      console.log("MEDIA:", data);

      setMedia({
        bannerUrl: data?.bannerUrl || "",
        gallery: Array.isArray(data?.gallery) ? data.gallery : [],
        slug: data?.slug || "",
      });
    } catch {
      // silent; venue may be new
    }
  };

  const loadGrounds = async () => {
    setLoadingGrounds(true);

    try {
      const list = await listVenueGrounds(venueId);
      setGrounds(Array.isArray(list) ? list : []);
    } catch {
      toast.error("Failed to load grounds");
    } finally {
      setLoadingGrounds(false);
    }
  };

  const handleCreateGround = async (payload) => {
    try {
      setBusy(true);

      const flat = {
        ...payload,
        basePrice:
          payload?.pricing?.basePrice === "" || payload?.pricing?.basePrice == null
            ? 0
            : Number(payload.pricing.basePrice),
        currency: payload?.pricing?.currency || "INR",
        unit: payload?.pricing?.unit || "hour",
      };

      delete flat.pricing;

      await createVenueGround(venueId, flat);

      toast.success("Ground added");
      setCreating(false);
      await loadGrounds();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Create failed");
      throw error;
    } finally {
      setBusy(false);
    }
  };

  const handleUpdateGround = async (groundId, payload) => {
    try {
      setBusyId(groundId);

      const flat = {
        ...payload,
        basePrice:
          payload?.pricing?.basePrice === "" || payload?.pricing?.basePrice == null
            ? 0
            : Number(payload.pricing.basePrice),
        currency: payload?.pricing?.currency || "INR",
        unit: payload?.pricing?.unit || "hour",
      };

      delete flat.pricing;

      await updateVenueGround(groundId, flat);

      toast.success("Ground updated");
      setEditing(null);
      await loadGrounds();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Update failed");
      throw error;
    } finally {
      setBusyId(null);
    }
  };

  const handleDeleteGround = async () => {
    const id = deleting?._id || deleting?.id;

    if (!id) {
      toast.error("Missing ground id");
      return;
    }

    try {
      setBusyId(id);
      await deleteVenueGround(id);

      toast.success("Ground deleted");
      setDeleting(null);
      await loadGrounds();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  const loadAdmins = async () => {
    if (!venueId) return;

    try {
      const items = await listAssignedVenueAdmins(venueId);

      const seen = new Set();
      const dedup = [];

      for (const admin of items) {
        const id = admin?.staff?._id || admin?._id;
        if (!id) continue;

        if (!seen.has(id)) {
          seen.add(id);
          dedup.push(admin);
        }
      }

      setAdmins(dedup);
    } catch {
      toast.error("Failed to load venue admins");
    }
  };

  const loadAllAdmins = async (q = "") => {
    try {
      const items = await listAllVenueAdmins({ q, active: true });
      setAllAdmins(items);
    } catch {
      setAllAdmins([]);
    }
  };

  const closeAdminModal = () => {
    if (savingAdmin) return;

    setAdminModalOpen(false);
    setAssignStaffId("");
    setAdminSearch("");
  };

  const handleCreateAndAssignAdmin = async (event) => {
    event?.preventDefault?.();

    try {
      if (!newAdmin.name || !newAdmin.email || !newAdmin.password) {
        toast.error("Please fill name, email, password");
        return;
      }

      setSavingAdmin(true);

      await createAndAssignVenueAdmin(venueId, { ...newAdmin, venueId });

      toast.success("Venue admin created & assigned");
      setNewAdmin({ name: "", email: "", password: "" });
      setAdminModalOpen(false);

      await loadAdmins();
      await loadAllAdmins();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        error?.message ||
        "Create/assign failed"
      );
    } finally {
      setSavingAdmin(false);
    }
  };

  const handleAssignExistingAdmin = async (event) => {
    event?.preventDefault?.();

    try {
      if (!assignStaffId) {
        toast.error("Choose an existing admin");
        return;
      }

      const picked = (allAdmins || []).find((admin) => admin._id === assignStaffId);

      if (picked && picked.venue && String(picked.venue) !== String(venueId)) {
        toast.error("This admin is already linked to another venue.");
        return;
      }

      setSavingAdmin(true);

      await assignExistingVenueAdmin(venueId, assignStaffId);

      toast.success("Admin assigned");
      setAssignStaffId("");
      setAdminModalOpen(false);

      await loadAdmins();
      await loadAllAdmins();
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Assign failed");
    } finally {
      setSavingAdmin(false);
    }
  };

  const handleUnassignAdmin = (staff) => {
    setUnassignTarget(staff);
  };

  const performUnassign = async () => {
    if (!unassignTarget?._id) {
      setUnassignTarget(null);
      return;
    }

    try {
      setUnassignSaving(true);

      await unassignVenueAdmin(venueId, unassignTarget._id);

      toast.success("Admin unassigned");
      setUnassignTarget(null);

      await loadAdmins();
      await loadAllAdmins();
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Unassign failed");
    } finally {
      setUnassignSaving(false);
    }
  };

  const openEditAdmin = (staff) => {
    setEditAdmin(staff);
    setEditForm({
      name: staff?.name || "",
      email: staff?.email || "",
      active: !!staff?.active,
    });
  };

  const handleEditAdminSubmit = async (event) => {
    event?.preventDefault?.();

    try {
      setEditSaving(true);

      const payload = {};

      if (editForm.name !== (editAdmin?.name || "")) {
        payload.name = editForm.name;
      }

      if (editForm.email !== (editAdmin?.email || "")) {
        payload.email = editForm.email;
      }

      if (
        typeof editForm.active === "boolean" &&
        editForm.active !== !!editAdmin?.active
      ) {
        payload.active = editForm.active;
      }

      if (Object.keys(payload).length === 0) {
        toast.info("No changes to save");
        setEditAdmin(null);
        return;
      }

      await updateVenueAdmin(editAdmin._id, payload);

      toast.success("Admin updated");
      setEditAdmin(null);

      await loadAdmins();
      await loadAllAdmins();
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Update failed");
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeleteBanner = async () => {
    if (!media?.slug) {
      toast.error("Missing venue slug. Please refresh.");
      setBannerDeleteOpen(false);
      return;
    }

    try {
      setBannerDeleting(true);

      await deleteBannerBySlug(media.slug);

      toast.success("Banner removed");
      setBannerDeleteOpen(false);

      await loadMedia();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Delete failed");
    } finally {
      setBannerDeleting(false);
    }
  };

  const handleDeleteGalleryImage = async () => {
    if (!media?.slug) {
      toast.error("Missing venue slug. Please refresh.");
      setGalleryDeleteTarget(null);
      return;
    }

    if (!galleryDeleteTarget?.key) {
      setGalleryDeleteTarget(null);
      return;
    }

    try {
      setGalleryDeleting(true);

      await deleteImageByKey(media.slug, galleryDeleteTarget.key);

      toast.success("Image removed");
      setGalleryDeleteTarget(null);

      await loadMedia();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Delete failed");
    } finally {
      setGalleryDeleting(false);
    }
  };

  const s3Post = async (url, fields, file) => {
    const form = new FormData();

    Object.entries(fields || {}).forEach(([key, value]) => {
      form.append(key, value);
    });

    form.append("file", file);

    const response = await fetch(url, { method: "POST", body: form });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`S3 upload failed: ${response.status} ${text}`);
    }
  };

  const handleUploadBanner = async (file) => {
    if (!file) return;

    try {
      setUploading(true);

      const presign = await presignMediaByVenueId(
        venueId,
        "banner",
        file.type || "image/jpeg"
      );

      await s3Post(presign.url, presign.fields, file);
      await confirmMediaByVenueId(venueId, "banner", presign.key);
      await loadMedia();
    } catch (error) {
      console.error(error);
      alert(error?.message || "Banner upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleUploadGallery = async (files) => {
    if (!files || files.length === 0) return;

    try {
      setUploading(true);

      for (const file of files) {
        const presign = await presignMediaByVenueId(
          venueId,
          "carousel",
          file.type || "image/jpeg"
        );

        await s3Post(presign.url, presign.fields, file);
        await confirmMediaByVenueId(venueId, "carousel", presign.key);
      }

      await loadMedia();
    } catch (error) {
      console.error(error);
      alert(error?.message || "Gallery upload failed");
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (!venueId) return;

    (async () => {
      await loadVenue();
      await loadGrounds();
      await loadMedia();

      if (!isVenueAdmin) {
        await loadAdmins();
        await loadAllAdmins();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId]);

  useEffect(() => {
    if (!media?.bannerUrl && bannerDeleteOpen) {
      setBannerDeleteOpen(false);
    }

    if (galleryDeleteTarget?.key) {
      const stillExists = (media?.gallery || []).some(
        (item) => item?.key === galleryDeleteTarget.key
      );

      if (!stillExists) {
        setGalleryDeleteTarget(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media]);

  const assignedCount = admins.length;
  const limitReached = assignedCount >= 2;

  const assignedIds = new Set(
    admins.map((admin) => admin?.staff?._id || admin?._id).filter(Boolean)
  );

  const availableAdmins = (allAdmins || [])
    .filter((admin) => !assignedIds.has(admin._id))
    .filter((admin) => !admin.venue || String(admin.venue) === String(venueId));

  const tc = useTableController(grounds, {
    initialPageSize: 10,
  });

  return (
    <div className="vd-page">
      <div className="sa-head-row vd-head-row">
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <h1 className="sa-h1">{venue?.name || "Venue Details"}</h1>

          <div style={{ maxWidth: 460 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "8px 12px",
                borderRadius: 14,
                border: "1px solid #d1d5db",
                background:
                  "linear-gradient(135deg, #f9fafb 0%, #e5e7eb 45%, #f9fafb 100%)",
                boxShadow:
                  "0 3px 6px rgba(15,23,42,0.12), 0 1px 0 rgba(255,255,255,0.7) inset",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  overflow: "hidden",
                }}
              >
                <span
                  style={{
                    fontSize: 11.5,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    color: "#6b7280",
                    fontWeight: 700,
                    opacity: 0.85,
                  }}
                >
                  Venue Slug
                </span>

                <span
                  title={venue?.slug || ""}
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#1f2937",
                    fontFamily:
                      "-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                    lineHeight: "1.35",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    letterSpacing: "0.01em",
                  }}
                >
                  {venue?.slug || "—"}
                </span>
              </div>

              {venue?.slug && (
                <button
                  type="button"
                  className="sa-btn"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 10px",
                    borderRadius: 999,
                    fontSize: 12,
                    lineHeight: 1,
                    backgroundColor: copiedSlug ? "#16a34a" : "#111827",
                    boxShadow: copiedSlug
                      ? "0 0 0 1px rgba(22,163,74,0.5), 0 4px 10px rgba(22,163,74,0.45)"
                      : "0 4px 10px rgba(15,23,42,0.28)",
                  }}
                  onClick={() => {
                    if (!venue?.slug) return;

                    const copySuccess = () => {
                      setCopiedSlug(true);
                      setTimeout(() => setCopiedSlug(false), 1500);
                      toast.success("Slug copied");
                    };

                    if (navigator?.clipboard?.writeText) {
                      navigator.clipboard
                        .writeText(venue.slug)
                        .then(copySuccess)
                        .catch(() => toast.error("Failed to copy slug"));
                    } else {
                      try {
                        const textArea = document.createElement("textarea");
                        textArea.value = venue.slug;
                        document.body.appendChild(textArea);
                        textArea.select();
                        document.execCommand("copy");
                        document.body.removeChild(textArea);
                        copySuccess();
                      } catch {
                        toast.error("Failed to copy slug");
                      }
                    }
                  }}
                >
                  <Copy className="lucide" size={14} />
                  <span>{copiedSlug ? "Copied" : "Copy"}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        <Link className="sa-btn-outline" to={toBase("/venues")}>
          ← Back
        </Link>
      </div>

      <div
        className="sa-card"
        style={{ padding: 0, overflow: "hidden", marginBottom: 16 }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 16px",
            borderBottom: "1px solid #eef1f5",
            background: "#fafbfd",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <h2 style={{ margin: 0 }}>{venue?.name || "—"}</h2>

            {venue?.active != null && (
              <span className={`sa-status ${venue.active ? "active" : "inactive"}`}>
                {venue.active ? "Active" : "Inactive"}
              </span>
            )}

            {Array.isArray(venue?.tags) && venue.tags.length > 0 && (
              <div className="sa-tags" style={{ marginLeft: 8 }}>
                {venue.tags.map((tag) => (
                  <span key={tag} className={tagClass(tag)}>
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div style={{ fontSize: 13, color: "#6b7280" }}>
            <span style={{ fontWeight: 600 }}>{venue?.location?.city || "—"}</span>
            <span className="sa-dot">•</span>
            <span>{venue?.location?.country || "—"}</span>
          </div>
        </div>

        <div
          className="vd-profile-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(420px, 1.2fr) 1fr",
            gap: 0,
          }}
        >
          <div style={{ padding: 14, borderRight: "1px solid #eef1f5" }}>
            <div
              style={{
                position: "relative",
                borderRadius: 12,
                overflow: "hidden",
                background: "#f3f4f6",
              }}
            >
              <div
                style={{
                  width: "100%",
                  aspectRatio: "16 / 9",
                  background: "#f3f4f6",
                }}
              >
                {media.bannerUrl ? (
                  <img
                    src={media.bannerUrl}
                    alt="Banner"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                ) : (
                  <div
                    className="sa-empty"
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    No banner uploaded
                  </div>
                )}
              </div>

              <div
                style={{
                  position: "absolute",
                  right: 8,
                  bottom: 8,
                  display: "flex",
                  gap: 8,
                }}
              >
                <label className="sa-btn" style={{ padding: "6px 10px", fontSize: 13 }}>
                  {uploading ? "Uploading…" : "Change Banner"}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    style={{ display: "none" }}
                    disabled={uploading}
                    onChange={(event) => handleUploadBanner(event.target.files?.[0])}
                  />
                </label>

                {media.bannerUrl && media.slug && (
                  <button
                    type="button"
                    className="sa-btn-outline"
                    style={{
                      padding: "6px 10px",
                      fontSize: 13,
                      background: "rgba(255,255,255,0.85)",
                    }}
                    onClick={() => {
                      if (uploading) return;
                      setBannerDeleteOpen(true);
                    }}
                    disabled={uploading}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>

            <div style={{ marginTop: 10 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <strong>Gallery</strong>

                <label className="sa-btn-outline" style={{ padding: "6px 10px", fontSize: 13 }}>
                  {uploading ? "Uploading…" : "Add Images"}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    style={{ display: "none" }}
                    disabled={uploading}
                    onChange={(event) =>
                      handleUploadGallery(Array.from(event.target.files || []))
                    }
                  />
                </label>
              </div>

              {media.gallery.length === 0 ? (
                <div className="sa-empty" style={{ marginTop: 8 }}>
                  No images yet.
                </div>
              ) : (
                <div
                  className="vd-gallery-grid"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(6, 1fr)",
                    gap: 8,
                    marginTop: 8,
                  }}
                >
                  {media.gallery.map((galleryItem) => (
                    <div
                      key={galleryItem.key}
                      className="sa-thumb"
                      style={{
                        position: "relative",
                        height: 72,
                        borderRadius: 8,
                        overflow: "hidden",
                        background: "#f3f4f6",
                      }}
                    >
                      <img
                        src={galleryItem.url}
                        alt=""
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />

                      <button
                        type="button"
                        className="sa-icon-link danger"
                        style={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          background: "rgba(255,255,255,0.9)",
                          borderRadius: 6,
                          padding: 2,
                        }}
                        title="Delete image"
                        onClick={() => {
                          if (!media?.slug) {
                            toast.error("Missing venue slug. Please refresh.");
                            return;
                          }

                          setGalleryDeleteTarget({
                            key: galleryItem.key,
                            url: galleryItem.url,
                          });
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={{ padding: 14 }}>
            <div
              className="vd-mini-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
              }}
            >
              <div className="sa-mini-card">
                <div className="sa-card-kicker">Location</div>

                <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                  <div>
                    <strong>Address:</strong> {venue?.location?.address || "—"}
                  </div>
                  <div>
                    <strong>City:</strong> {venue?.location?.city || "—"}
                  </div>
                  <div>
                    <strong>Country:</strong> {venue?.location?.country || "—"}
                  </div>
                  <div>
                    <strong>Lat/Lng:</strong>{" "}
                    {venue?.location?.latitude ?? "—"}/
                    {venue?.location?.longitude ?? "—"}
                  </div>

                  {venue?.googleMapsUrl && (
                    <a
                      href={venue.googleMapsUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="sa-link"
                      style={{
                        marginTop: 6,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 12,
                        fontWeight: 500,
                        color: "#2563eb",
                      }}
                    >
                      <MapPin size={14} />
                      <span>Open in Google Maps</span>
                    </a>
                  )}
                </div>
              </div>

              <div className="sa-mini-card">
                <div className="sa-card-kicker">Timings & Price</div>

                <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                  <div>
                    <strong>Open:</strong> {venue?.policies?.openTime || "—"}
                  </div>
                  <div>
                    <strong>Close:</strong> {venue?.policies?.closeTime || "—"}
                  </div>
                  <div>
                    <strong>Base Price:</strong> {venue?.pricing?.basePrice ?? "—"}
                  </div>
                </div>
              </div>

              <div className="sa-mini-card">
                <div className="sa-card-kicker">Contact</div>

                <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                  <div>
                    <strong>Phone:</strong> {formatContactPhone(venue?.contact)}
                  </div>
                  <div>
                    <strong>Label:</strong> {venue?.contact?.label || "—"}
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <div className="sa-card-kicker">Facilities</div>

              {venue?.facilities?.length ? (
                <div className="sa-tags">
                  {venue.facilities.map((facility) => (
                    <span key={facility} className="sa-tag">
                      {facility.replace("_", " ")}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="sa-gray">—</div>
              )}
            </div>

            <div style={{ marginTop: 12 }}>
              <div className="sa-card-kicker">About</div>

              <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                {venue?.shortDescription ? (
                  <p style={{ margin: "6px 0" }}>{venue.shortDescription}</p>
                ) : null}

                <p style={{ margin: "6px 0", color: "#4b5563" }}>
                  {venue?.fullDescription || (
                    <span className="sa-gray">No description provided.</span>
                  )}
                </p>
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <div className="sa-card-kicker">Policies</div>

              <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                <div>
                  <strong>Cancellation:</strong>{" "}
                  {venue?.policies?.cancellationPolicy || "—"}
                </div>
                <div>
                  <strong>Rules/Terms:</strong>{" "}
                  {venue?.policies?.rulesTerms || "—"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section>
        <div className="sa-panel-hd">
          <h2>Grounds & Pricing</h2>

          <button type="button" className="sa-btn" onClick={() => setCreating(true)}>
            + Add Ground
          </button>
        </div>

        <div className="sa-card" style={{ marginTop: 10 }}>
          {loadingGrounds ? (
            <div className="sa-placeholder" style={{ height: 120 }}>
              Loading grounds…
            </div>
          ) : grounds.length === 0 ? (
            <div className="sa-empty">No grounds yet.</div>
          ) : tc.rows.length === 0 ? (
            <div className="sa-empty">No grounds available.</div>
          ) : (
            <>
              <div className="sa-table-wrap">
                <table className="sa-table">
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>S.N.</th>

                      <th onClick={() => tc.toggleSort("name")} style={{ cursor: "pointer" }}>
                        Ground Name{" "}
                        {tc.sortKey === "name"
                          ? tc.sortDir === "asc"
                            ? "▲"
                            : "▼"
                          : ""}
                      </th>

                      <th onClick={() => tc.toggleSort("type")} style={{ cursor: "pointer" }}>
                        Type{" "}
                        {tc.sortKey === "type"
                          ? tc.sortDir === "asc"
                            ? "▲"
                            : "▼"
                          : ""}
                      </th>

                      <th
                        onClick={() => tc.toggleSort("capacity")}
                        style={{ cursor: "pointer" }}
                      >
                        Capacity{" "}
                        {tc.sortKey === "capacity"
                          ? tc.sortDir === "asc"
                            ? "▲"
                            : "▼"
                          : ""}
                      </th>

                      <th
                        onClick={() => tc.toggleSort("basePrice")}
                        style={{ cursor: "pointer" }}
                      >
                        Base Price{" "}
                        {tc.sortKey === "basePrice"
                          ? tc.sortDir === "asc"
                            ? "▲"
                            : "▼"
                          : ""}
                      </th>

                      <th>Currency</th>
                      <th>Unit</th>
                      <th style={{ textAlign: "right", width: 110 }}>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {tc.rows.map((ground, index) => {
                      const id = ground._id || ground.id;
                      const serialNumber = (tc.page - 1) * tc.pageSize + index + 1;

                      return (
                        <tr key={id}>
                          <td>{serialNumber}</td>
                          <td>{ground.name}</td>
                          <td>{ground.type?.replace("_", " ")}</td>
                          <td>{ground.capacity ?? "—"}</td>
                          <td>{ground.basePrice ?? "—"}</td>
                          <td>{ground.currency ?? "—"}</td>
                          <td>{ground.unit ?? "—"}</td>

                          <td className="sa-actions-cell">
                            <div className="sa-actions">
                              <button
                                type="button"
                                className="sa-icon-link"
                                title="Manage pricing"
                                onClick={() => openPricing(ground)}
                                disabled={busyId === id}
                              >
                                <WalletCards className="lucide" />
                              </button>

                              <button
                                type="button"
                                className="sa-icon-link"
                                title="Edit ground"
                                onClick={() =>
                                  setEditing({
                                    ...ground,
                                    pricing: {
                                      basePrice: ground?.basePrice ?? "",
                                      currency: ground?.currency || "INR",
                                      unit: ground?.unit || "hour",
                                    },
                                  })
                                }
                                disabled={busyId === id}
                              >
                                <Pencil className="lucide" />
                              </button>

                              <button
                                type="button"
                                className="sa-icon-link danger"
                                title="Delete ground"
                                onClick={() => setDeleting(ground)}
                                disabled={busyId === id}
                              >
                                <Trash2 className="lucide" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <Paginator
                page={tc.page}
                totalPages={tc.totalPages}
                onPage={tc.setPage}
                pageSize={tc.pageSize}
                onPageSizeChange={tc.setPageSize}
              />
            </>
          )}
        </div>
      </section>

      {creating && (
        <VenueGroundForm
          open={creating}
          mode="create"
          onCancel={() => {
            if (busy) return;
            setCreating(false);
          }}
          onSubmit={handleCreateGround}
          venueCurrency={venue?.pricing?.currency || "INR"}
          existingGrounds={grounds}
          venue={venue}
        />
      )}

      {editing && (
        <VenueGroundForm
          key={editing?._id || editing?.id}
          open={!!editing}
          mode="edit"
          initialData={editing}
          venueCurrency={venue?.pricing?.currency || "INR"}
          existingGrounds={grounds}
          venue={venue}
          onCancel={() => {
            if (busyId === (editing?._id || editing?.id)) return;
            setEditing(null);
          }}
          onSubmit={(payload) =>
            handleUpdateGround(editing?._id || editing?.id, payload)
          }
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        title="Delete Ground"
        message={`Are you sure you want to delete "${deleting?.name || "this ground"
          }"? This action will remove the ground from this venue.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        loading={busyId === (deleting?._id || deleting?.id)}
        onConfirm={handleDeleteGround}
        onCancel={() => {
          if (busyId !== (deleting?._id || deleting?.id)) {
            setDeleting(null);
          }
        }}
      />

      <ConfirmDialog
        open={bannerDeleteOpen}
        title="Remove Banner"
        message="This will remove the banner image from this venue."
        confirmText="Remove"
        cancelText="Cancel"
        confirmVariant="danger"
        loading={bannerDeleting}
        onConfirm={handleDeleteBanner}
        onCancel={() => {
          if (!bannerDeleting) setBannerDeleteOpen(false);
        }}
      />

      <ConfirmDialog
        open={!!galleryDeleteTarget}
        title="Remove Image"
        message="This will remove the selected image from the gallery."
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        loading={galleryDeleting}
        onConfirm={handleDeleteGalleryImage}
        onCancel={() => {
          if (!galleryDeleting) setGalleryDeleteTarget(null);
        }}
      />

      <PricingModal
        open={pricingOpen}
        onClose={closePricing}
        ground={pricingGround}
      />

      {isSuperAdmin && (
        <VenueWebsiteBuilderModal
          open={websiteBuilderOpen}
          venue={venue}
          media={media}
          grounds={grounds}
          submitting={websiteSaving}
          onClose={() => {
            if (!websiteSaving) setWebsiteBuilderOpen(false);
          }}
          onSubmit={savePublicWebsite}
        />
      )}

      {!isVenueAdmin && (
        <section style={{ marginTop: 18 }}>
          <div className="sa-panel-hd">
            <h2>Venue Admins</h2>

            <div
              className="vd-admin-actions"
              style={{ display: "flex", gap: 8, alignItems: "center" }}
            >
              <span className="sa-gray" style={{ fontSize: 13 }}>
                Assigned: <strong>{assignedCount}</strong>/2
              </span>

              <button
                type="button"
                className="sa-btn"
                onClick={() => {
                  if (limitReached) {
                    toast.info(
                      "This venue already has 2 admins. Unassign one to add another."
                    );
                    return;
                  }

                  setAdminMode("create");
                  setAdminModalOpen(true);
                }}
                disabled={limitReached}
                title={
                  limitReached
                    ? "Limit reached (2/2). Unassign one to add another."
                    : ""
                }
              >
                + Create & Assign
              </button>

              <button
                type="button"
                className="sa-btn-outline"
                onClick={() => {
                  if (limitReached) {
                    toast.info(
                      "This venue already has 2 admins. Unassign one to add another."
                    );
                    return;
                  }

                  setAdminMode("assign");
                  setAdminModalOpen(true);
                }}
                disabled={limitReached}
                title={
                  limitReached
                    ? "Limit reached (2/2). Unassign one to add another."
                    : ""
                }
              >
                + Assign Existing
              </button>
            </div>
          </div>

          {limitReached && (
            <div
              className="sa-alert"
              style={{
                marginTop: 10,
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 12px",
                border: "1px solid #fde68a",
                background: "#fffbeb",
                borderRadius: 8,
                color: "#92400e",
                fontSize: 13,
              }}
            >
              <AlertCircle className="lucide" />
              <div>This venue already has 2 admins. Unassign one to add another.</div>
            </div>
          )}

          <div className="sa-card" style={{ marginTop: 10 }}>
            {admins.length === 0 ? (
              <div className="sa-empty">No admins assigned to this venue yet.</div>
            ) : (
              <div className="sa-table-wrap">
                <table className="sa-table">
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>S.N.</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Assigned On</th>
                      <th style={{ textAlign: "right", width: 140 }}>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {admins.map((admin, index) => {
                      const staff = admin.staff || admin;
                      const assignedAt =
                        admin.assignedAt || admin.createdAt || staff?.createdAt;

                      return (
                        <tr key={staff?._id || index}>
                          <td>{index + 1}</td>
                          <td>{staff?.name || "—"}</td>
                          <td>{staff?.email || "—"}</td>
                          <td>{staff?.role || "venue_admin"}</td>

                          <td>
                            <span
                              className={`sa-status ${staff?.active ? "active" : "inactive"
                                }`}
                            >
                              {staff?.active ? "Active" : "Inactive"}
                            </span>
                          </td>

                          <td>
                            {assignedAt ? new Date(assignedAt).toLocaleString() : "—"}
                          </td>

                          <td className="sa-actions-cell">
                            <div className="sa-actions">
                              <button
                                type="button"
                                className="sa-icon-link"
                                title="Edit admin"
                                onClick={() => openEditAdmin(staff)}
                              >
                                <Pencil className="lucide" />
                              </button>

                              <button
                                type="button"
                                className="sa-icon-link danger"
                                title="Unassign admin"
                                onClick={() => handleUnassignAdmin(staff)}
                              >
                                <UserMinus className="lucide" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <CreateAssignVenueAdminModal
            open={adminModalOpen && adminMode === "create"}
            values={newAdmin}
            saving={savingAdmin}
            onChange={setNewAdmin}
            onClose={closeAdminModal}
            onSubmit={handleCreateAndAssignAdmin}
          />

          <AssignExistingVenueAdminModal
            open={adminModalOpen && adminMode === "assign"}
            adminSearch={adminSearch}
            assignStaffId={assignStaffId}
            availableAdmins={availableAdmins}
            saving={savingAdmin}
            onSearchChange={async (event) => {
              const q = event.target.value;
              setAdminSearch(q);
              await loadAllAdmins(q);
            }}
            onSelectAdmin={(event) => setAssignStaffId(event.target.value)}
            onClose={closeAdminModal}
            onSubmit={handleAssignExistingAdmin}
          />

          <EditVenueAdminModal
            open={!!editAdmin}
            values={editForm}
            saving={editSaving}
            onChange={setEditForm}
            onClose={() => {
              if (!editSaving) setEditAdmin(null);
            }}
            onSubmit={handleEditAdminSubmit}
          />

          <ConfirmDialog
            open={!!unassignTarget}
            title="Unassign Admin"
            message={`Are you sure you want to unassign "${unassignTarget?.name || "this admin"
              }" from this venue? Their account will remain active.`}
            confirmText="Unassign"
            cancelText="Cancel"
            confirmVariant="danger"
            loading={unassignSaving}
            onConfirm={performUnassign}
            onCancel={() => {
              if (!unassignSaving) setUnassignTarget(null);
            }}
          />
        </section>
      )}

      {isSuperAdmin && (
        <section className="vd-public-site-section">
        <div className="sa-panel-hd vd-public-site-header">
          <div>
            <h2>Public Website Settings</h2>
            <p>
              Configure the venue’s public page, booking experience, visible
              sections, and SEO.
            </p>
          </div>

          <span
            className={`sa-status vd-public-site-status ${venue?.publicWebsite?.enabled === false
                ? "inactive"
                : (venue?.publicWebsite?.status || "published") === "published"
                  ? "active"
                  : ""
              }`}
          >
            {venue?.publicWebsite?.enabled === false
              ? "unpublished"
              : venue?.publicWebsite?.status || "published"}
          </span>
        </div>

        <div className="sa-card vd-public-site-card">
          <div className="vd-public-site-content">
            <div className="vd-public-site-details">
              <div className="vd-public-site-label">Public venue URL</div>

              <div
                className={`vd-public-site-url ${!getPublicVenueUrl() ? "is-unavailable" : ""
                  }`}
                title={
                  getPublicVenueUrl() ||
                  "Public website URL is not available yet."
                }
              >
                {getPublicVenueUrl() ||
                  "URL will be available after a venue slug is created."}
              </div>

              <div className="vd-public-site-note">
                Changes made here control how customers view the venue and
                start a booking.
              </div>
            </div>

            <div className="vd-public-site-actions">
              <button
                type="button"
                className="sa-btn"
                onClick={() => setWebsiteBuilderOpen(true)}
              >
                Configure Website
              </button>

              <button
                type="button"
                className="sa-btn-outline"
                onClick={copyPublicVenueUrl}
                disabled={!getPublicVenueUrl()}
              >
                <Copy className="lucide" />
                Copy Link
              </button>

              {getPublicVenueUrl() ? (
                <a
                  className="sa-btn-outline"
                  href={getPublicVenueUrl()}
                  target="_blank"
                  rel="noreferrer"
                  style={{ textDecoration: "none" }}
                >
                  Open Website
                </a>
              ) : (
                <button
                  type="button"
                  className="sa-btn-outline"
                  disabled
                >
                  Open Website
                </button>
              )}
            </div>
          </div>
        </div>
        </section>
      )}

      <style>{`

              .vd-public-site-section {
          margin-top: 18px;
        }

        .vd-public-site-header {
          align-items: flex-start;
        }

        .vd-public-site-header h2 {
          margin: 0;
        }

        .vd-public-site-header p {
          margin: 4px 0 0;
          color: #6b7280;
          font-size: 13px;
          line-height: 1.5;
        }

        .vd-public-site-status {
          flex-shrink: 0;
          text-transform: capitalize;
        }

        .vd-public-site-card {
          margin-top: 10px;
          padding: 20px;
          overflow: hidden;
          background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
        }

        .vd-public-site-content {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          align-items: center;
          gap: 24px;
        }

        .vd-public-site-details {
          min-width: 0;
        }

        .vd-public-site-label {
          margin-bottom: 8px;
          color: #6b7280;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }

        .vd-public-site-url {
          width: fit-content;
          max-width: 100%;
          padding: 10px 12px;
          overflow: hidden;
          color: #111827;
          background: #ffffff;
          border: 1px solid #dbe3ee;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          line-height: 1.4;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .vd-public-site-url.is-unavailable {
          color: #9ca3af;
          font-weight: 500;
        }

        .vd-public-site-note {
          margin-top: 8px;
          color: #6b7280;
          font-size: 12px;
          line-height: 1.5;
        }

        .vd-public-site-actions {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: flex-end;
          gap: 8px;
        }

        .vd-public-site-actions .sa-btn,
        .vd-public-site-actions .sa-btn-outline {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          min-height: 38px;
          white-space: nowrap;
        }

        @media (max-width: 760px) {
          .vd-public-site-content {
            grid-template-columns: 1fr;
            gap: 16px;
          }

          .vd-public-site-actions {
            justify-content: flex-start;
          }

          .vd-public-site-url {
            width: 100%;
            white-space: normal;
            word-break: break-word;
          }
        }

        .va-form,
        .va-grid,
        .va-grid > *,
        .va-col-span-2,
        .va-actions {
          box-sizing: border-box;
          max-width: 100%;
          min-width: 0;
        }

        .va-form {
          display: flex;
          flex-direction: column;
          gap: 18px;
          width: 100%;
          overflow-x: hidden;
        }

        .va-sub {
          color: #6b7280;
          font-size: 13px;
          line-height: 1.45;
        }

        .va-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 18px;
          align-items: start;
          width: 100%;
          overflow-x: hidden;
        }

        .va-col-span-2 {
          grid-column: 1 / -1;
        }

        .va-tip {
          border: 1px dashed #d9e2ec;
          border-radius: 14px;
          background: #f8fafc;
          padding: 14px;
        }

        .va-tip-title {
          font-weight: 800;
          font-size: 13px;
          margin-bottom: 8px;
          color: #374151;
        }

        .va-tip-list {
          margin: 0;
          padding-left: 18px;
          color: #6b7280;
          font-size: 13px;
          line-height: 1.5;
        }

        .va-status-row {
          display: flex;
          align-items: center;
          gap: 10px;
          min-height: 48px;
        }

        .va-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding-top: 8px;
          border-top: 1px solid #e8edf3;
        }

        .vd-page .sa-table-wrap {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        .vd-page .vd-head-row {
          align-items: flex-start;
          gap: 12px;
        }

        @media (max-width: 980px) {
          .vd-page .vd-profile-grid {
            grid-template-columns: 1fr !important;
          }

          .vd-page .vd-profile-grid > div:first-child {
            border-right: none !important;
            border-bottom: 1px solid #eef1f5;
          }
        }

        @media (max-width: 980px) {
          .vd-page .vd-gallery-grid {
            grid-template-columns: repeat(4, 1fr) !important;
          }
        }

        @media (max-width: 520px) {
          .vd-page .vd-gallery-grid {
            grid-template-columns: repeat(3, 1fr) !important;
          }
        }

        @media (max-width: 380px) {
          .vd-page .vd-gallery-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }

        @media (max-width: 720px) {
          .va-grid {
            grid-template-columns: minmax(0, 1fr);
            gap: 17px;
          }

          .va-col-span-2 {
            grid-column: auto;
          }

          .va-actions {
            flex-direction: column;
          }

          .va-actions .fs-btn {
            width: 100%;
          }

          .vd-page .vd-mini-grid {
            grid-template-columns: 1fr !important;
          }
        }

        @media (max-width: 820px) {
          .vd-page .sa-table {
            min-width: 760px;
          }
        }

        .vd-page .sa-actions {
          display: inline-flex;
          gap: 8px;
          flex-wrap: nowrap;
          align-items: center;
        }

        @media (max-width: 720px) {
          .vd-page .vd-head-row {
            flex-direction: column;
            align-items: stretch;
          }

          .vd-page .vd-head-row > a.sa-btn-outline {
            align-self: flex-start;
          }
        }

        @media (max-width: 520px) {
          .vd-page .vd-head-row > div {
            width: 100%;
          }
        }

        .vd-page .vd-admin-actions {
          display: flex;
          gap: 8px;
          align-items: center;
        }
          @media (max-width: 820px) {
  .va-create-assign-modal,
  .va-assign-existing-modal,
  .va-edit-admin-modal,
  .va-unassign-admin-modal {
    margin-top: auto !important;
    margin-bottom: auto !important;
  }
}
         

        @media (max-width: 720px) {
          .vd-page .sa-panel-hd {
            flex-wrap: wrap;
            gap: 10px;
            align-items: flex-start;
          }

          .vd-page .vd-admin-actions {
            width: 100%;
            flex-wrap: wrap;
            justify-content: flex-start;
          }

          .vd-page .vd-admin-actions .sa-btn,
          .vd-page .vd-admin-actions .sa-btn-outline {
            width: 100%;
            justify-content: center;
            white-space: nowrap;
          }

          .vd-page .vd-admin-actions .sa-gray {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default VenueDetails;